import React from 'react';
import { Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { DataVehicles, GetPersonUser, NormalizedVoucher, PersonBenefits } from '@esportal/portal-service';
import { getExpectedAuthenticatedRoute, isAllowedOnboardingRoute } from './get-expected-auth-route';

interface GuardProps {
	userData: GetPersonUser | null;
	hasToLog?: boolean;
	userVehicles?: DataVehicles | null;
	personBenefits?: PersonBenefits | null;
	voucherId?: string;
}

// helper para parsear de forma segura el parámetro de redirección
const getValidRedirectUrl = (searchParams: URLSearchParams): string | null => {
	const redirect = searchParams.get('redirectTo');
	const isInternalRoute = (route: string) => route.startsWith('/') && !route.startsWith('//');
	return redirect && isInternalRoute(redirect) ? redirect : null;
};

// auth guard -> redirige a /login si el usuario no está logueado
export const RequireAuth = ({ userData, hasToLog }: GuardProps) => {
	const location = useLocation();

	if (hasToLog || !userData) {
		// si el usuario acaba de cerrar sesión, redirigimos a /login sin parámetros de redirección
		if (sessionStorage.getItem('userHasJustLoggedOut') === 'true') {
			sessionStorage.removeItem('userHasJustLoggedOut');
			return <Navigate to="/login" replace />;
		}

		const currentRoute = `${location.pathname}${location.search}${location.hash}`;
		if (currentRoute !== '/') {
			const loginUrl = `/login?redirectTo=${encodeURIComponent(currentRoute)}`;
			return <Navigate to={loginUrl} replace />;
		} else {
			return <Navigate to="/login" replace />;
		}
	}

	return <Outlet />;
};

// club guard -> gestiona el acceso a rutas exclusivas para miembros del club (/dashboard) +
// redirige a pasos intermedios según el estado del usuario y sus vehículos
export const RequireClubMember = ({ userData, userVehicles }: GuardProps) => {
	const expectedRoute = getExpectedAuthenticatedRoute(userData, userVehicles);

	if (expectedRoute !== '/dashboard') {
		return <Navigate to={expectedRoute} replace />;
	}

	return <Outlet />;
};

// guard para rutas "solo públicas" -> redirige a /dashboard (o ruta de on boarding correspondiente) después de hacer login
export const PublicOnly = ({ userData, hasToLog, userVehicles }: GuardProps) => {
	const location = useLocation();
	const [searchParams] = useSearchParams();

	if (userData && !hasToLog) {
		const expectedRoute = getExpectedAuthenticatedRoute(userData, userVehicles);
		const redirectTo = getValidRedirectUrl(searchParams);

		if (redirectTo && expectedRoute === '/dashboard') {
			return <Navigate to={redirectTo} replace />;
		}

		if (location.pathname !== expectedRoute) {
			return <Navigate to={expectedRoute} replace />;
		}
	}

	return <Outlet />;
};

// guard para rutas "solo no miembros" -> redirige a /dashboard si el usuario ya es miembro del club
export const RequireOnboardingStep = ({ userData, userVehicles }: GuardProps) => {
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const expectedRoute = getExpectedAuthenticatedRoute(userData, userVehicles);

	if (expectedRoute === '/dashboard') {
		const redirectTo = getValidRedirectUrl(searchParams);

		if (redirectTo) {
			return <Navigate to={redirectTo} replace />;
		}

		return <Navigate to="/dashboard" replace />;
	}

	if (!isAllowedOnboardingRoute(location.pathname, expectedRoute)) {
		return <Navigate to={expectedRoute} replace />;
	}

	return <Outlet />;
};

// guard para la ruta de error inesperado -> redirige a la ruta esperada si el usuario no debería estar aquí
export const UnexpectedErrorOnly = ({ userData, userVehicles }: GuardProps) => {
	const expectedRoute = getExpectedAuthenticatedRoute(userData, userVehicles);

	if (expectedRoute !== '/unexpected-error') {
		return <Navigate to={expectedRoute} replace />;
	}

	return <Outlet />;
};

// guard para la ruta de voucher -> redirige a /dashboard si el voucher no existe o el usuario no tiene acceso a él
export const RequireVoucher = ({ normalizedVouchers }: { normalizedVouchers: NormalizedVoucher[] | null }) => {
	const { id: voucherId } = useParams<{ id: string }>();

	if (!normalizedVouchers || !voucherId || !normalizedVouchers.find((voucher) => voucher.id === voucherId)) {
		return <Navigate to="/dashboard" replace />;
	}

	return <Outlet />;
};

// guard para redirigir una url de campaña al voucher correspondiente
export const CampaignRedirector = ({ normalizedVouchers }: { normalizedVouchers: NormalizedVoucher[] | null }) => {
	const { id: campaignId } = useParams<{ id: string }>();

	if (!normalizedVouchers || !campaignId) {
		return <Navigate to="/dashboard" replace />;
	}

	// filtrar todos los vouchers que pertenecen a esta campaña
	const campaignVouchers = normalizedVouchers.filter((v) => v.campaign === campaignId);

	if (campaignVouchers.length === 0) {
		return <Navigate to="/dashboard" replace />;
	}

	// obtenemos el primer voucher
	const topVoucher = campaignVouchers[0]; // normalizedVouchers ya viene ordenado por prioridad

	return <Navigate to={`/voucher/salesforce/${topVoucher.id}`} replace />;
};

// guard para rutas no encontradas -> redirige a la ruta esperada según el estado del usuario
export const NotFoundRedirect = ({ userData, userVehicles }: GuardProps) => {
	const expectedRoute = getExpectedAuthenticatedRoute(userData, userVehicles);
	return <Navigate to={expectedRoute} replace />;
};
